import { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';

/**
 * The funnel button and its two-pane filter popover.
 *
 * Lives here rather than inside `DashboardHeaderSlotContext` because plain pages
 * want the same control: dashboards reach it through `DashboardHeaderSlot`,
 * while a page drops it straight into its `PageHeader` actions. Either way a
 * user looking for filters looks in one place and finds one thing.
 *
 * `sections` — array of `{ id, label, content, active? }`.
 * `active` — shows a dot on the trigger icon.
 * `onReset` — optional; shows a "Reset all" row while any filter is active.
 */
export function FilterPopover({ sections, active, onReset }) {
    const [open, setOpen] = useState(false);
    const [activeId, setActiveId] = useState(sections[0]?.id ?? null);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    if (!sections.length) return null;
    // Falls back rather than syncing state in an effect: when the caller swaps
    // its sections out, the selection simply lands on the first one.
    const activeSec = sections.find((s) => s.id === activeId) ?? sections[0];

    return (
        <div ref={ref} className="relative flex items-center">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="relative rounded-md border border-border bg-card p-2 text-muted-foreground shadow-sm transition-colors hover:border-primary-300 hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                aria-label="Open filters"
                aria-expanded={open}
                aria-haspopup="dialog"
            >
                <Filter className="h-4 w-4" aria-hidden />
                {active ? (
                    <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary-600 ring-2 ring-card"
                        aria-hidden
                    />
                ) : null}
            </button>

            <div
                className={`absolute right-0 top-full z-[60] mt-1.5 w-[min(calc(100vw-2rem),22rem)] origin-top-right overflow-visible rounded-xl border border-border bg-card shadow-xl transition-all ${
                    open
                        ? 'visible scale-100 opacity-100'
                        : 'invisible scale-95 opacity-0 pointer-events-none'
                }`}
                role="dialog"
                aria-label="Filters"
            >
                <div className="flex min-h-[10rem]">
                    {/* Left nav */}
                    <div className="flex w-24 flex-shrink-0 flex-col border-r border-border bg-muted/30 py-2">
                        <p className="px-3 pb-2 pt-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                            Filters
                        </p>
                        {sections.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => setActiveId(s.id)}
                                className={`relative flex items-center gap-2 px-3 py-2.5 text-left text-xs font-medium transition-colors ${
                                    s.id === activeId
                                        ? 'bg-card text-primary-700 shadow-sm dark:text-primary-300'
                                        : 'text-muted-foreground hover:bg-card/70 hover:text-foreground'
                                }`}
                            >
                                {s.id === activeId ? (
                                    <span
                                        className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-primary-600"
                                        aria-hidden
                                    />
                                ) : null}
                                <span className="truncate">{s.label}</span>
                                {s.active ? (
                                    <span
                                        className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500"
                                        aria-hidden
                                    />
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {/* Content pane */}
                    <div className="min-w-0 flex-1 overflow-visible p-4">
                        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            {activeSec?.label}
                        </p>
                        <div key={activeSec?.id} className="overflow-visible">
                            {activeSec?.content}
                        </div>
                    </div>
                </div>
                {active && onReset ? (
                    <div className="border-t border-border px-4 py-2.5 flex items-center justify-end">
                        <button
                            type="button"
                            onClick={() => { onReset(); setOpen(false); }}
                            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40"
                        >
                            Reset all filters
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default FilterPopover;
